/**
 * Confetti effects (86bc2cgxd).
 *
 * loadConfetti()/celebrate() are the applicant dashboard's bursts. js-confetti is
 * loaded from the CDN on demand rather than bundled; the version is pinned (it used
 * to be @latest) so a new release can never change a live page. rainConfetti() is
 * the landlord congrats page's brand-coloured effect and needs no library.
 */
export const CONFETTI_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/js-confetti@0.13.1/dist/js-confetti.browser.js'

/**
 * Resolves once the library is available, or once loading failed — confetti is
 * decoration, so a blocked CDN must never block the page.
 */
export function loadConfetti() {
    return new Promise((resolve) => {
        if (window.JSConfetti) {
            resolve()

            return
        }

        const script = document.createElement('script')
        script.src = CONFETTI_SCRIPT_URL
        script.onload = () => resolve()
        script.onerror = () => resolve()
        document.head.appendChild(script)
    })
}

/**
 * A colour burst, then an emoji burst. A no-op when the library did not load.
 */
export function celebrate() {
    if (! window.JSConfetti) {
        return
    }

    try {
        const jsConfetti = new window.JSConfetti()

        jsConfetti.addConfetti({
            confettiRadius: 6,
            confettiNumber: 300,
            confettiColors: [
                '#10b981', // green-500
                '#22c55e', // green-400
                '#86efac', // green-300
                '#3b82f6', // blue-500
                '#fbbf24', // yellow-400
                '#f59e0b', // amber-500
            ],
        })

        setTimeout(() => {
            jsConfetti.addConfetti({
                emojis: ['🎉', '✨', '🎊', '🏠', '🔑'],
                emojiSize: 60,
                confettiNumber: 30,
            })
        }, 200)
    } catch (error) {
        console.error('Confetti error:', error)
    }
}

/**
 * Brand gold, with a little navy, for the landlord congrats page (Rich, 2026-09-24).
 */
export const BRAND_CONFETTI_COLORS = ['#bb9733', '#d4b35a', '#e8c872', '#f1dfa7', '#2B3960']

/**
 * Gold paper strips falling across the whole page, like Rich's congrats mockup.
 * Drawn with plain elements and the Web Animations API, so nothing is loaded.
 * Skipped when the visitor asks for reduced motion.
 *
 * @returns {() => void} removes the strips early (e.g. when leaving the page)
 */
export function rainConfetti({ count = 110, colors = BRAND_CONFETTI_COLORS, random = Math.random } = {}) {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
        return () => {}
    }

    const layer = document.createElement('div')
    layer.setAttribute('aria-hidden', 'true')
    layer.dataset.test = 'confetti-rain'
    layer.style.cssText = 'position:fixed;inset:0;pointer-events:none;overflow:hidden;z-index:60'

    let longest = 0

    for (let i = 0; i < count; i++) {
        const strip = document.createElement('span')
        const width = 6 + random() * 6
        const duration = 2600 + random() * 2200
        const delay = random() * 1400
        const drift = (random() - 0.5) * 160
        const spin = (random() < 0.5 ? -1 : 1) * (360 + random() * 540)

        longest = Math.max(longest, duration + delay)
        strip.style.cssText = [
            'position:absolute',
            'top:0',
            `left:${random() * 100}%`,
            `width:${width}px`,
            `height:${width * 2.2}px`,
            `background:${colors[Math.floor(random() * colors.length)]}`,
            'border-radius:1px',
            'opacity:0',
        ].join(';')

        strip.animate?.([
            { transform: 'translate3d(0, -5vh, 0) rotate(0deg)', opacity: 1 },
            { transform: `translate3d(${drift}px, 105vh, 0) rotate(${spin}deg)`, opacity: 1 },
        ], { duration, delay, easing: 'cubic-bezier(.25,.6,.5,1)', fill: 'both' })

        layer.appendChild(strip)
    }

    document.body.appendChild(layer)

    const timer = setTimeout(() => layer.remove(), longest + 100)

    return () => {
        clearTimeout(timer)
        layer.remove()
    }
}
